import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { PrintView, fmtEUR } from "./InvoiceCommon";

const EMPTY_FOOTER = { fixed: "", custom: "" };
import { CheckCircle2, Loader2, ShieldCheck, Mail, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignDevis() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", devis: null, company: null, prefill: null });
  const [step, setStep] = useState(1); // 1 identity, 2 otp, 3 signature
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [signerRole, setSignerRole] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signedOk, setSignedOk] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpResendTimer, setOtpResendTimer] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/devis/sign/${token}`);
        setState({ loading: false, error: "", devis: r.data.devis, company: r.data.company, prefill: r.data.client_prefill, footer: r.data.pdf_footer });
        if (r.data.client_prefill?.email) setEmail(r.data.client_prefill.email);
        if (r.data.client_prefill?.phone) setPhone(r.data.client_prefill.phone);
      } catch (e) {
        setState({ loading: false, error: e?.response?.data?.detail || "Lien invalide", devis: null, company: null, prefill: null });
      }
    })();
  }, [token]);

  // Timer de renvoi OTP
  useEffect(() => {
    if (otpResendTimer <= 0) return;
    const t = setTimeout(() => setOtpResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendTimer]);

  const brandColor = state.company?.primary_color || "#0284c7";
  const d = state.devis;
  const c = state.company || {};
  const alreadySigned = d?.status === "accepted" || d?.status === "cancelled";

  const requestOtp = async () => {
    setFormErr("");
    if (!firstName.trim() || !lastName.trim()) return setFormErr("Nom et prénom obligatoires.");
    if (!email.trim()) return setFormErr("Email obligatoire.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/devis/sign/${token}/request-otp`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
      });
      setOtpSent(true);
      setOtpResendTimer(30);
      setStep(2);
    } catch (err) {
      setFormErr(err?.response?.data?.detail || "Erreur lors de l'envoi du code.");
    } finally { setSubmitting(false); }
  };

  const verifyOtpLocal = () => {
    setFormErr("");
    if (!otpCode.trim() || otpCode.trim().length < 6) return setFormErr("Merci de saisir le code à 6 chiffres reçu par email.");
    setStep(3);
  };

  const submitSignature = async (e) => {
    e.preventDefault();
    setFormErr("");
    if (!accepted) return setFormErr("Vous devez cocher « Bon pour accord » pour signer.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/devis/sign/${token}`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        signer_role: signerRole.trim(),
        email: email.trim(),
        phone: phone.trim(),
        otp_code: otpCode.trim(),
        accepted: true,
      });
      setSignedOk(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur — veuillez réessayer.";
      // Si code incorrect / expiré → revenir à l'étape OTP
      if (msg.toLowerCase().includes("code")) setStep(2);
      setFormErr(msg);
    } finally { setSubmitting(false); }
  };

  const stepPill = (n, label, IconComp) => {
    const active = step === n;
    const done = step > n;
    const base = "flex items-center gap-2 text-[11px] font-semibold px-3 py-1.5 rounded-full transition-colors";
    if (done) return <div className={`${base} bg-emerald-100 text-emerald-700`}><CheckCircle2 className="h-3.5 w-3.5" /> {label}</div>;
    if (active) return <div className={`${base} text-white`} style={{ backgroundColor: brandColor }}><IconComp className="h-3.5 w-3.5" /> {label}</div>;
    return <div className={`${base} bg-slate-100 text-slate-500`}><IconComp className="h-3.5 w-3.5" /> {label}</div>;
  };

  if (state.loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement du devis…</div>
      </div>
    );
  }
  if (state.error || !state.devis) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md bg-white rounded-md border border-rose-200 p-6 text-center">
          <div className="text-rose-700 font-semibold">Lien invalide</div>
          <div className="text-sm text-slate-600 mt-2">{state.error || "Ce lien de signature est expiré ou inconnu."}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-16" style={{ "--brand-primary": brandColor }} data-testid="sign-devis-page">
      {/* En-tête */}
      <div className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {c.logo_file_id && <img src={`${API}/brand/logo`} alt="logo" className="h-10 object-contain" />}
            <div>
              <div className="font-display font-bold text-lg" style={{ color: brandColor }}>{c.name || "Prestataire"}</div>
              <div className="text-[11px] text-slate-500">{c.brand_tagline || "Signature en ligne sécurisée"}</div>
            </div>
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-1"><ShieldCheck className="h-4 w-4" style={{ color: brandColor }} /> Signature avec vérification par email</div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {signedOk ? (
          <div className="bg-white rounded-md border border-emerald-200 p-8 text-center shadow-sm">
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600 mb-3" />
            <h1 className="font-display text-2xl font-extrabold text-slate-900">Devis signé, merci !</h1>
            <p className="text-sm text-slate-600 mt-2">
              Le devis <b>{d.number}</b> a bien été accepté par <b>{firstName} {lastName}</b>.
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Un email de confirmation a été envoyé à <b>{email}</b>. L'équipe {c.name || ""} vient d'être notifiée.
            </p>
          </div>
        ) : (
          <>
            {/* Récap devis */}
            <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-display text-2xl font-extrabold" style={{ color: brandColor }}>Devis {d.number}</div>
                  <div className="text-xs text-slate-500">Émis le {d.date}{d.valid_until ? ` · Valable jusqu'au ${d.valid_until}` : ""}</div>
                </div>
                <button type="button" onClick={()=>setShowPreview(true)} className="text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50" data-testid="btn-open-preview">
                  Voir l'aperçu complet (A4)
                </button>
              </div>
              <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Émetteur</div>
                  <div className="font-semibold">{c.legal_name || c.name || "—"}</div>
                  <div className="text-xs text-slate-600 whitespace-pre-line">{[c.address, [c.postal_code, c.city].filter(Boolean).join(" ")].filter(Boolean).join("\n")}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Destinataire</div>
                  <div className="font-semibold">{d.client_name}</div>
                  {d.client_address && <div className="text-xs text-slate-600 whitespace-pre-line">{d.client_address}</div>}
                </div>
                {(d.site_name || d.site_address) && (
                  <div className="md:col-span-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Site concerné</div>
                    <div className="text-sm font-semibold">{d.site_name}</div>
                    {d.site_address && <div className="text-xs text-slate-600 whitespace-pre-line">{d.site_address}</div>}
                  </div>
                )}
              </div>
              <div className="px-6 pb-4">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                    <tr><th className="py-2 text-left">Description</th><th className="py-2">Qté</th><th className="py-2">PU HT</th><th className="py-2 text-right">Total HT</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {d.items.map((it, i) => (
                      <tr key={i}>
                        <td className="py-1.5">{it.description}</td>
                        <td className="py-1.5 text-center font-mono whitespace-nowrap">{it.quantity} {it.unit || ""}</td>
                        <td className="py-1.5 text-center font-mono whitespace-nowrap">{fmtEUR(it.unit_price_ht)}</td>
                        <td className="py-1.5 text-right font-mono font-semibold whitespace-nowrap">{fmtEUR(it.quantity * it.unit_price_ht)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-slate-200 text-xs">
                    <tr><td colSpan={3} className="py-1 text-right">Total HT</td><td className="py-1 text-right font-mono font-semibold">{fmtEUR(d.total_ht)}</td></tr>
                    <tr><td colSpan={3} className="py-1 text-right">TVA</td><td className="py-1 text-right font-mono font-semibold">{fmtEUR(d.total_vat)}</td></tr>
                    <tr className="border-t border-slate-900"><td colSpan={3} className="py-2 text-right font-display font-extrabold" style={{ color: brandColor }}>Total TTC</td><td className="py-2 text-right font-mono font-extrabold" style={{ color: brandColor }}>{fmtEUR(d.total_ttc)}</td></tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Bloc signature multi-étapes */}
            <div className="mt-6 bg-white rounded-md border border-slate-200 p-6 shadow-sm" data-testid="sign-form">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {stepPill(1, "Identification", Mail)}
                <div className="h-px w-6 bg-slate-200" />
                {stepPill(2, "Code email", KeyRound)}
                <div className="h-px w-6 bg-slate-200" />
                {stepPill(3, "Signature", CheckCircle2)}
              </div>

              {alreadySigned && (
                <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2 mb-4">
                  Ce devis a déjà été traité (statut : {d.status}). Vous ne pouvez plus le signer.
                </div>
              )}

              {/* --- ÉTAPE 1 : IDENTIFICATION --- */}
              {step === 1 && !alreadySigned && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Prénom *</div>
                      <input data-testid="sign-firstname" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={firstName} onChange={(e)=>setFirstName(e.target.value)} placeholder="Ex : Marie" />
                    </label>
                    <label className="block">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Nom *</div>
                      <input data-testid="sign-lastname" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={lastName} onChange={(e)=>setLastName(e.target.value)} placeholder="Ex : Bernard" />
                    </label>
                    <label className="block">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Email *</div>
                      <input data-testid="sign-email" type="email" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="marie@exemple.com" />
                    </label>
                    <label className="block">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Téléphone (facultatif)</div>
                      <input data-testid="sign-phone" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="06 12 34 56 78" />
                    </label>
                  </div>
                  {formErr && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{formErr}</div>}
                  <button
                    onClick={requestOtp}
                    disabled={submitting || !firstName.trim() || !lastName.trim() || !email.trim()}
                    data-testid="sign-request-otp"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-semibold text-white disabled:opacity-60"
                    style={{ backgroundColor: brandColor }}
                  >
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi…</> : <><Mail className="h-4 w-4" /> Recevoir mon code par email</>}
                  </button>
                  <p className="text-[11px] text-slate-500 mt-2">Nous vous enverrons un code à 6 chiffres à cette adresse pour confirmer votre identité.</p>
                </div>
              )}

              {/* --- ÉTAPE 2 : CODE OTP --- */}
              {step === 2 && !alreadySigned && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-700">Un code à 6 chiffres a été envoyé à <b>{email}</b>. Il est valable 15 minutes.</p>
                  <label className="block">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Code de vérification *</div>
                    <input
                      data-testid="sign-otp"
                      inputMode="numeric"
                      maxLength={6}
                      className="w-full max-w-[220px] border border-slate-300 rounded-md px-3 py-2 text-center text-2xl font-mono tracking-[10px]"
                      value={otpCode}
                      onChange={(e)=>setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="••••••"
                    />
                  </label>
                  {formErr && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{formErr}</div>}
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={verifyOtpLocal} disabled={otpCode.length < 6} data-testid="sign-verify-otp" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: brandColor }}>
                      <CheckCircle2 className="h-4 w-4" /> Valider le code
                    </button>
                    <button onClick={requestOtp} disabled={otpResendTimer > 0 || submitting} data-testid="sign-resend-otp" className="inline-flex items-center gap-1 px-4 py-2.5 rounded-md text-sm font-semibold border border-slate-300 hover:bg-slate-50 disabled:opacity-60">
                      {otpResendTimer > 0 ? `Renvoyer (${otpResendTimer}s)` : "Renvoyer le code"}
                    </button>
                    <button onClick={()=>setStep(1)} className="inline-flex items-center gap-1 px-4 py-2.5 rounded-md text-sm text-slate-600 hover:bg-slate-50">← Modifier mes infos</button>
                  </div>
                </div>
              )}

              {/* --- ÉTAPE 3 : SIGNATURE FINALE --- */}
              {step === 3 && !alreadySigned && (
                <form onSubmit={submitSignature} className="space-y-3">
                  <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
                    <div><b>Signataire :</b> {firstName} {lastName}</div>
                    <div className="text-xs text-slate-600">{email}{phone ? ` · ${phone}` : ""}</div>
                  </div>
                  <label className="block">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Fonction</div>
                    <input data-testid="sign-role" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={signerRole} onChange={(e)=>setSignerRole(e.target.value)} placeholder="Ex : Directrice" />
                  </label>
                  <label className="flex items-start gap-2 text-sm text-slate-700 select-none">
                    <input data-testid="sign-bpa" type="checkbox" className="mt-0.5 h-4 w-4" checked={accepted} onChange={(e)=>setAccepted(e.target.checked)} />
                    <span>
                      <b>Bon pour accord</b> — Je reconnais avoir pris connaissance du devis n° <b>{d.number}</b> pour un montant total de <b>{fmtEUR(d.total_ttc)}</b> TTC et je l'accepte sans réserve.
                    </span>
                  </label>
                  {formErr && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{formErr}</div>}
                  <button
                    type="submit"
                    data-testid="sign-submit"
                    disabled={submitting || !accepted}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-semibold text-white disabled:opacity-60"
                    style={{ backgroundColor: brandColor }}
                  >
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Signature…</> : <><CheckCircle2 className="h-4 w-4" /> Signer et accepter le devis</>}
                  </button>
                </form>
              )}
            </div>
          </>
        )}
      </div>

      {showPreview && <PrintView kind="devis" doc={d} company={c} footer={state.footer || EMPTY_FOOTER} onClose={()=>setShowPreview(false)} />}
    </div>
  );
}
